import * as React from 'react';
import S, { DataSignal } from 's-js';
import { SuperCriticalComponent } from 'lib/supercritical';

export interface NumericInputProps<TValue> {
    value : DataSignal<TValue>;
    className? : string;
    disabled? : boolean;
    tabindex? : number;
    placeholder? : string;
    autoFocus? : boolean;
    onFocus? : () => void;
    onBlur? : () => void;
    inputRef? : (el : HTMLInputElement | null) => void;
}

abstract class NumericInputBase<TValue extends number | null> extends SuperCriticalComponent<NumericInputProps<TValue>> {
    readonly writtenValue = S.data('');
    readonly dirty = S.data(false);
    abstract readonly allowNullSets : boolean;

    constructor(props : NumericInputProps<TValue>) {
        super(props);
        S.root(() => S.on(this.writtenValue, () => {
            let num = this.numberify(this.writtenValue());
            if (num !== null && !isNaN(num as number) || num === null && this.allowNullSets) {
                this.sprops().value(num);
            }
        }, null, true));
    }

    srender(props : NumericInputProps<TValue>) {
        return (
            <input
                ref={props.inputRef}
                className={props.className}
                value={this.dirty() ? this.writtenValue() : this.stringify(props.value())}
                onChange={e => S.freeze(() => {
                    this.dirty(true);
                    this.writtenValue(e.currentTarget.value);
                })}
                disabled={props.disabled}
                tabIndex={props.tabindex}
                onFocus={props.onFocus}
                onBlur={() => S.freeze(() => {
                    this.dirty(false);
                    props.onBlur && props.onBlur();
                })}
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
            />
        );
    }

    abstract numberify(val : string) : TValue;
    abstract stringify(val : TValue) : string;
}

export class NumericInput extends NumericInputBase<number> {
    allowNullSets = false;

    stringify(val : number) : string {
        return '' + val;
    }

    numberify(val : string) {
        return parseFloat(val);
    }
}

export class NullableNumericInput extends NumericInputBase<number | null> {
    allowNullSets = true;

    stringify(val : number) : string {
        return val === null ? '' : '' + val;
    }

    numberify(val : string) {
        return val === '' ? null : parseFloat(val);
    }
}

export class IntegerInput extends NumericInputBase<number> {
    allowNullSets = false;

    stringify(val : number) : string {
        return '' + val;
    }

    numberify(val : string) {
        return parseInt(val);
    }
}

export class NullableIntegerInput extends NumericInputBase<number | null> {
    allowNullSets = true;

    stringify(val : number) : string {
        return val === null ? '' : '' + val;
    }

    numberify(val : string) {
        return val === '' ? null : parseInt(val);
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/numericinput/view.tsx